/**
 * Roving Tab/Space keyboard navigation for MCQ question panels.
 *
 * @module useMcqKeyboardNavigation
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefCallback,
} from 'react';
import type { Question } from '@/types/exam-simulation';
import {
  buildMcqFocusTargets,
  findQuestionFocusIndex,
  getMcqFocusDomId,
  getQuestionIdForFocusTarget,
  stepFocusIndex,
  type McqFocusTarget,
} from '@/utils/mcqKeyboardNavigation';

export interface McqFocusBindingProps {
  readonly tabIndex: 0 | -1;
  readonly 'data-mcq-focus-id': string;
  readonly ref: RefCallback<HTMLElement>;
  readonly onFocus: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

interface UseMcqKeyboardNavigationOptions {
  readonly questions: readonly Question[];
  readonly struckAnswers: Record<number, string[]>;
  readonly activeQuestionId: number | null;
  readonly enabled: boolean;
  readonly onQuestionSelect: (questionId: number) => void;
  readonly onAnswerSelect: (questionId: number, answerId: string) => void;
}

interface UseMcqKeyboardNavigationReturn {
  readonly focusTargets: readonly McqFocusTarget[];
  readonly isTargetFocused: (domId: string) => boolean;
  readonly getFocusProps: (domId: string) => McqFocusBindingProps | null;
  readonly onEntryFocus: () => void;
}

export function useMcqKeyboardNavigation({
  questions,
  struckAnswers,
  activeQuestionId,
  enabled,
  onQuestionSelect,
  onAnswerSelect,
}: UseMcqKeyboardNavigationOptions): UseMcqKeyboardNavigationReturn {
  const focusTargets = useMemo(
    () => (enabled ? buildMcqFocusTargets(questions, struckAnswers) : []),
    [enabled, questions, struckAnswers],
  );

  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const elementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const focusIndexRef = useRef<number | null>(null);
  const lastSyncedActiveQuestionRef = useRef<number | null>(null);

  focusIndexRef.current = focusIndex;

  const focusElementAt = useCallback((index: number) => {
    const target = focusTargets[index];
    if (!target) {
      return;
    }
    const domId = getMcqFocusDomId(target);
    elementRefs.current.get(domId)?.focus();
  }, [focusTargets]);

  const syncActiveQuestion = useCallback(
    (index: number) => {
      const questionId = getQuestionIdForFocusTarget(focusTargets, index);
      if (questionId != null && questionId !== activeQuestionId) {
        onQuestionSelect(questionId);
      }
    },
    [activeQuestionId, focusTargets, onQuestionSelect],
  );

  const setFocusAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= focusTargets.length) {
        return;
      }
      setFocusIndex(index);
      syncActiveQuestion(index);
      focusElementAt(index);
    },
    [focusElementAt, focusTargets, syncActiveQuestion],
  );

  const activateTargetAt = useCallback(
    (index: number) => {
      const target = focusTargets[index];
      if (!target) {
        return;
      }
      if (target.kind === 'question') {
        onQuestionSelect(target.questionId);
        return;
      }
      onQuestionSelect(target.questionId);
      onAnswerSelect(target.questionId, target.optionId);
    },
    [focusTargets, onAnswerSelect, onQuestionSelect],
  );

  const handleKeyDown = useCallback(
    (index: number, event: KeyboardEvent<HTMLElement>) => {
      if (!enabled || focusTargets.length === 0) {
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        const next = stepFocusIndex(index, event.shiftKey ? -1 : 1, focusTargets.length);
        setFocusAt(next);
        return;
      }

      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        activateTargetAt(index);
      }
    },
    [activateTargetAt, enabled, focusTargets.length, setFocusAt],
  );

  const registerRef = useCallback((domId: string): RefCallback<HTMLElement> => {
    return (element) => {
      if (element) {
        elementRefs.current.set(domId, element);
      } else {
        elementRefs.current.delete(domId);
      }
    };
  }, []);

  const getFocusProps = useCallback(
    (domId: string): McqFocusBindingProps | null => {
      const index = focusTargets.findIndex((t) => getMcqFocusDomId(t) === domId);
      if (index === -1) {
        return null;
      }

      return {
        tabIndex: focusIndex === index ? 0 : -1,
        'data-mcq-focus-id': domId,
        ref: registerRef(domId),
        onFocus: () => {
          setFocusIndex(index);
          syncActiveQuestion(index);
        },
        onKeyDown: (event) => handleKeyDown(index, event),
      };
    },
    [focusIndex, focusTargets, handleKeyDown, registerRef, syncActiveQuestion],
  );

  const isTargetFocused = useCallback(
    (domId: string) => {
      const index = focusTargets.findIndex((t) => getMcqFocusDomId(t) === domId);
      return index !== -1 && focusIndex === index;
    },
    [focusIndex, focusTargets],
  );

  const onEntryFocus = useCallback(() => {
    if (!enabled || focusTargets.length === 0) {
      return;
    }
    setFocusAt(0);
  }, [enabled, focusTargets.length, setFocusAt]);

  // Clamp focus when targets shrink (e.g. struck option removed).
  useEffect(() => {
    if (focusIndex === null) {
      return;
    }
    if (focusTargets.length === 0) {
      setFocusIndex(null);
      return;
    }
    if (focusIndex >= focusTargets.length) {
      const next = focusTargets.length - 1;
      setFocusIndex(next);
      focusElementAt(next);
    }
  }, [focusIndex, focusElementAt, focusTargets.length]);

  // Sync keyboard focus when parent sets active question (passage click, navigator jump).
  useEffect(() => {
    if (!enabled || activeQuestionId == null) {
      lastSyncedActiveQuestionRef.current = activeQuestionId;
      return;
    }
    if (lastSyncedActiveQuestionRef.current === activeQuestionId) {
      return;
    }
    lastSyncedActiveQuestionRef.current = activeQuestionId;

    const questionIndex = findQuestionFocusIndex(focusTargets, activeQuestionId);
    if (questionIndex === -1) {
      return;
    }

    setFocusIndex(questionIndex);
    focusElementAt(questionIndex);
  }, [activeQuestionId, enabled, focusElementAt, focusTargets]);

  // Reset when section questions change or navigation is disabled.
  useEffect(() => {
    if (!enabled) {
      setFocusIndex(null);
      elementRefs.current.clear();
      lastSyncedActiveQuestionRef.current = null;
    }
  }, [enabled]);

  useEffect(() => {
    setFocusIndex(null);
    lastSyncedActiveQuestionRef.current = null;
  }, [questions]);

  return {
    focusTargets,
    isTargetFocused,
    getFocusProps,
    onEntryFocus,
  };
}
